import { TThunkAction } from '../../types/redux';
import {
  fetchSaveUserEstimationScoreFact,
  ISaveUserEstimationScoreFactRequest,
  ISaveUserEstimationScoreFactResponse200 as IRes200,
} from '../../repositories/price-estimator/v1/save-user-estimation-score-fact';
import { setRateOurEstimationScreen, toggleRateOurEstimationLoading, toggleRateOurModal } from './actions';
import { ERateScreen } from '../../types/redux/rateOurEstimation';
import { trackSaveScoreRate } from './tracking';
import { BadRequestError } from '@cian/valuation-utils-component';
import { EPlatform } from '@cian/webview/shared';
import { mapPageTypeToAnalytics } from '../../utils/getPageviewObject';
import {
  fetchPublicSuburbanV1SaveUserEstimationScoreFact,
  IPublicSuburbanV1SaveUserEstimationScoreFactResponse200 as ISuburbanRes200,
} from '../../repositories/price-estimator/suburban/v1/save-user-estimation-score-fact';
import { ECategory } from '../../../shared/repositories/price-estimator/entities/estimation_suburban_score_fact/EstimationSuburbanScoreSchema';

export function saveScoreRateOurEstimation(): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const {
      filters: { area, roomsCount, houseId },
      page: { dealType, deviceType, pageType },
      rateOurEstimation: { rateOurEstimationValue, userPrice, comment },
    } = getState();
    const { httpApi, logger } = context;
    const platform = context.config.get<EPlatform | null>('web.platform') ?? null;

    if (!area || !roomsCount || !houseId) {
      return;
    }

    dispatch(toggleRateOurEstimationLoading(true));

    const parameters: ISaveUserEstimationScoreFactRequest = {
      area,
      roomsCount,
      houseId,
      dealType,
    };

    try {
      const res = await fetchSaveUserEstimationScoreFact<IRes200>({
        httpApi,
        parameters,
      });

      if (res.statusCode === 200) {
        dispatch(setRateOurEstimationScreen(ERateScreen.Thanks));
        dispatch(toggleRateOurModal(false));

        trackSaveScoreRate({
          label: rateOurEstimationValue,
          deviceType,
          fairprice: userPrice,
          comment,
          platform,
          houseId,
          pageType: mapPageTypeToAnalytics[pageType],
        });

        return;
      }

      throw new BadRequestError({
        domain: 'saveScoreRateOurEstimation.ts',
        message: res.response.message as string,
        details: { errors: JSON.stringify(res.response.errors) },
      });
    } catch (error) {
      logger.error(error, { message: 'Failed get price-estimator/v1/save-user-estimation-score-fact', ...parameters });
    } finally {
      dispatch(toggleRateOurEstimationLoading(false));
    }
  };
}

export function saveSuburbanScoreRateOurEstimation(): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const {
      filters: { address, category, landArea, houseArea, houseId },
      page: { deviceType, pageType },
      rateOurEstimation: { rateOurEstimationValue, userPrice, comment },
    } = getState();
    const { httpApi, logger } = context;
    const platform = context.config.get<EPlatform | null>('web.platform') ?? null;

    dispatch(toggleRateOurEstimationLoading(true));

    const parameters = {
      address,
      category: category as unknown as ECategory,
      landArea: landArea as number,
      houseArea,
      houseId,
    };

    try {
      const res = await fetchPublicSuburbanV1SaveUserEstimationScoreFact<ISuburbanRes200>({
        httpApi,
        parameters,
      });

      if (res.statusCode === 200) {
        dispatch(setRateOurEstimationScreen(ERateScreen.Thanks));
        dispatch(toggleRateOurModal(false));

        trackSaveScoreRate({
          label: rateOurEstimationValue,
          deviceType,
          fairprice: userPrice,
          comment,
          platform,
          houseId,
          pageType: mapPageTypeToAnalytics[pageType],
        });

        return;
      }

      throw new BadRequestError({
        domain: 'saveSuburbanScoreRateOurEstimation.ts',
        message: res.response.message as string,
        details: { errors: JSON.stringify(res.response.errors) },
      });
    } catch (error) {
      logger.error(error, {
        message: 'Failed get price-estimator/suburban/v1/save-user-estimation-score-fact',
        ...parameters,
      });
    } finally {
      dispatch(toggleRateOurEstimationLoading(false));
    }
  };
}
